/**
 * Breached-password check (spec §5.1) — k-anonymity range query against the
 * Pwned Passwords API, chosen over relying on GoTrue's leaked-password
 * protection because it lets the portal show the "this password has been
 * seen in N breaches" message inline, at the point of account creation,
 * without a dashboard toggle to keep in sync between environments. Ruling
 * recorded in portal/notes/T10.md (not portal/PLAN.md — see the SHARED DOC
 * FILES rule in .superpowers/…/global-constraints.md). Only the SHA-1 hash
 * *prefix* (5 hex chars) is ever sent — the API never sees the password or
 * the full hash, and connect-src to api.pwnedpasswords.com is
 * business-origin only (the admin build has no self-service account
 * creation, so it never calls this).
 *
 * **Fails open, deliberately, and this is a real gap, not an oversight**:
 * a network failure/CORS block/offline visitor sets `checkFailed: true` and
 * the caller (SignInForm, invite.accept) proceeds anyway rather than
 * blocking account creation on a third-party API being reachable — but it
 * means a breached password sails through uncaught whenever the check
 * can't run. It also only ever runs from THIS client code path: nothing
 * stops a direct `supabase.auth.signUp()` call (a hand-rolled script, a
 * different client, a future code path that doesn't go through
 * `SignInForm`) from bypassing it entirely — there is no server-side
 * enforcement. See portal/notes/T10.md for the residual-risk note this
 * should feed into a future security review.
 */

async function sha1Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-1', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
}

export interface BreachCheckResult {
  breached: boolean;
  count: number;
  /** The check itself failed to run (network/CSP/offline) — callers must
   * not block account creation on an infra failure, only on a genuine hit. */
  checkFailed: boolean;
}

export async function checkPasswordBreached(password: string): Promise<BreachCheckResult> {
  try {
    const hash = await sha1Hex(password);
    const prefix = hash.slice(0, 5);
    const suffix = hash.slice(5);
    const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`);
    if (!res.ok) return { breached: false, count: 0, checkFailed: true };
    const body = await res.text();
    for (const line of body.split('\n')) {
      const [suf, count] = line.trim().split(':');
      if (suf === suffix) return { breached: true, count: Number(count) || 0, checkFailed: false };
    }
    return { breached: false, count: 0, checkFailed: false };
  } catch {
    return { breached: false, count: 0, checkFailed: true };
  }
}
